<template>
  <!-- ATTENTION: every change in size must be reflected in the item-size of the VirtualScroller -->
  <div
    class="py-1"
    :class="{
      'pr-2': !plain,
      'cursor-pointer': !node.disabled && (node.selectable == null || node.selectable),
    }"
    :style="`padding-left: ${plain ? 0 : (node.path.split('.').length - 1) * (smallIdent ? 1.125 : 1.5)}em`"
    :data-testid="`tree-select-item-${node.key}`"
    @click="elementClicked"
    @dragover.stop="$emit('dragOverCustom', { event: $event, target: 'sameLevel' })"
  >
    <div
      class="flex justify-content-between py-1 px-2"
      :class="{
        highlight: highlightChild,
      }"
    >
      <div v-if="!plain" style="width: 1.8em; flex-shrink: 0">
        <i
          v-if="node.children == null || node.children.length === 0"
          class="fas fa-chevron-right pr-2"
          style="color: transparent"
          @click.stop
        />
        <i
          v-else-if="collapsed[node.key]"
          class="fas fa-chevron-right pr-2"
          @click.stop="delete collapsed[node.key]"
        />
        <i v-else class="fas fa-chevron-down pr-4" @click.stop="collapsed[node.key] = true" />
      </div>

      <div
        class="w-100 flex align-items-center gap-2 justify-content-start text-truncate"
        :style="{
          background:
            node.disabled || node.hidden
              ? 'var(--p-disabled-color)'
              : selected[node.key] === true
                ? 'var(--p-highlight-background)'
                : '',
          outline:
            node.disabled || node.hidden
              ? '0.325rem solid var(--p-disabled-color)'
              : selected[node.key] === true
                ? '0.325rem solid var(--p-highlight-background)'
                : '',
          borderRadius: 'var(--p-border-radius-md)',
          borderTopRightRadius: showDropChildIconComputed ? 0 : 'var(--p-border-radius-md)',
          borderBottomRightRadius: showDropChildIconComputed ? 0 : 'var(--p-border-radius-md)',
        }"
      >
        <div
          v-if="!hideCheckbox"
          class="flex justify-content-center align-items-center border-1"
          style="
            width: 1.2em;
            height: 1.2em;
            border-radius: var(--p-border-radius-md);
            border-color: var(--p-gray-400);
          "
          :class="{
            'bg-primary': isSelected,
          }"
        >
          <i
            class="fas"
            :class="checkBoxIcon"
            :style="{
              color: selected[props.node.key] === true ? 'var(--p-primary-contrast-color)' : '',
            }"
            style="font-size: 0.75em"
          />
        </div>

        <div
          v-tooltip.bottom="{ disabled: node.tooltip == null, value: node.tooltip }"
          class="text-truncate"
          :class="{ 'text-muted': node.disabled || node.hidden }"
        >
          <i v-if="node.beforeIcon">
            <i :class="node.beforeIcon" :style="{ color: 'var(--p-primary-color)' }" />
          </i>
          <b v-if="!hidePath">{{ node.path }} |</b>
          {{ node.label }}
          <template v-if="showId">[ID: {{ node.key }}]</template>

          <i v-if="node.afterIcon" :class="node.afterIcon" />
        </div>
        <div class="flex-shrink-0" @click.stop>
          <slot name="controls" :node="node" />
        </div>
      </div>
      <div
        v-if="showDropChildIconComputed"
        class="px-2"
        :style="{
          background:
            node.disabled || node.hidden
              ? 'var(--p-disabled-color)'
              : selected[node.key] === true
                ? 'var(--p-highlight-background)'
                : '',
          outline:
            node.disabled || node.hidden
              ? '0.325rem solid var(--p-disabled-color)'
              : selected[node.key] === true
                ? '0.325rem solid var(--p-highlight-background)'
                : '',

          borderRadius: 'var(--p-border-radius-md)',
          borderTopLeftRadius: 0,
          borderBottomLeftRadius: 0,
        }"
        @dragover.stop="$emit('dragOverCustom', { event: $event, target: 'child' })"
      >
        <i class="fas fa-arrows-down-to-line" style="color: var(--p-gray-300)" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, type PropType } from "vue";
import type { TreeNodeItem } from "./TreeSelectHelper";
import vTooltip from "primevue/tooltip";

const showId = true;

const props = defineProps({
  node: {
    type: Object as PropType<TreeNodeItem>,
    required: true,
  },
  multiple: {
    type: Boolean,
    required: false,
    default: false,
  },
  plain: {
    type: Boolean,
    required: false,
    default: false,
  },
  hideCheckbox: {
    type: Boolean,
    required: false,
    default: false,
  },
  hidePath: {
    type: Boolean,
    required: false,
    default: false,
  },
  smallIdent: {
    type: Boolean,
    required: false,
    default: false,
  },
  showDropChildIcon: {
    type: Boolean,
    required: false,
    default: false,
  },
  highlightChild: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const showDropChildIconComputed = computed(
  () => props.showDropChildIcon && props.node.path.includes("."),
);

defineEmits<{
  dragOverCustom: [{ event: DragEvent; target: "child" | "sameLevel" }];
}>();

const selected = defineModel<Record<string, boolean>>("selected", {
  required: true,
  default: () => ({}) as Record<string, boolean>,
});

const collapsed = defineModel("collapsed", {
  type: Object as PropType<Record<string, boolean>>,
  required: true,
  default: () => ({}) as Record<string, boolean>,
});

const isSelected = computed({
  get(): boolean {
    return !!checkBoxIcon.value && selected.value[props.node.key] === true;
  },
  set(value: boolean) {
    if (value) {
      selected.value[props.node.key] = true;
    } else {
      delete selected.value[props.node.key];
    }
  },
});

function isChildSelected(node: TreeNodeItem): "partial" | boolean {
  if (!node.children || node.children.length === 0) {
    return false;
  }
  let someChildsSelected = false;
  let allChildsSelected = true;
  for (const child of node.children) {
    const childSelected = selected.value[child];
    if (childSelected === true) {
      someChildsSelected = true;
    } else {
      allChildsSelected = false;
    }
  }
  return someChildsSelected && !allChildsSelected ? "partial" : allChildsSelected;
}

const checkBoxIcon = computed(() => {
  const selfSelected = selected.value[props.node.key] === true;
  let childSelected = isChildSelected(props.node);

  if (childSelected === true && !selfSelected) {
    return "fa-minus";
  } else if (childSelected === false && selfSelected) {
    return "fa-check";
  } else if (childSelected === true && selfSelected) {
    return "fa-check-double";
  } else if (childSelected === "partial") {
    return "fa-minus";
  } else {
    return "";
  }
});

function setChildKeysFromSelected(remove = true) {
  if (props.node.children && props.node.children.length > 0) {
    for (const child of props.node.children) {
      if (remove) {
        delete selected.value[child];
      } else {
        selected.value[child] = true;
      }
    }
  }
}

function elementClicked() {
  if (props.node.disabled || props.node.selectable === false) {
    return;
  }

  if (!props.multiple) {
    for (const key in selected.value) {
      if (key !== props.node.key) {
        delete selected.value[key];
      }
    }
    if (selected.value[props.node.key] === true) {
      delete selected.value[props.node.key];
    } else {
      selected.value = { ...selected.value, [props.node.key]: true };
    }
    return;
  }

  switch (checkBoxIcon.value) {
    case "fa-check-double":
      delete selected.value[props.node.key];
      setChildKeysFromSelected();
      break;
    case "fa-check":
      if (props.node.children == null || props.node.children.length === 0) {
        delete selected.value[props.node.key];
      } else {
        setChildKeysFromSelected(false);
      }
      break;
    case "fa-minus":
      if (selected.value[props.node.key] !== true) {
        selected.value[props.node.key] = true;
      } else {
        setChildKeysFromSelected(false);
      }
      break;
    default:
      selected.value[props.node.key] = true;
      break;
  }
}
</script>

<style lang="scss" scoped>
.highlight {
  box-shadow: inset 0 -2px 0 var(--p-green-200);
}
</style>
